import { useEffect, useState, useRef } from "react";
import { Phone, Upload, Plus, Trash2, Loader2, CheckCircle2, XCircle, Link2, User, Building2, Copy, Download } from "lucide-react";
import { toast } from "sonner";
import { NavLink } from "react-router-dom";
import { http, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

const EMPTY_ROW = () => ({
  id: Math.random().toString(36).slice(2),
  linkedin_url: "",
  first_name: "",
  last_name: "",
  domain: "",
  state: "idle",         // idle | pending | settled | refunded | error
  phone: null,
  lookup_id: null,
  reason: null,
});

export default function FindMobile() {
  const [mode, setMode] = useState("manual"); // "manual" | "csv"
  const [rows, setRows] = useState([EMPTY_ROW(), EMPTY_ROW()]);
  const [balance, setBalance] = useState(0);
  const [running, setRunning] = useState(false);
  const fileRef = useRef(null);

  const loadBalance = async () => {
    try {
      const { data } = await http.get("/mobile-credits/balance");
      setBalance(data.balance || 0);
    } catch (_) {}
  };
  useEffect(() => { loadBalance(); }, []);

  const rowIsValid = (r) =>
    !!(r.linkedin_url?.trim()) ||
    (r.first_name?.trim() && r.last_name?.trim() && r.domain?.trim());

  const validRowCount = rows.filter(rowIsValid).length;

  const updateRow = (id, patch) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const addRow = () => setRows((rs) => [...rs, EMPTY_ROW()]);
  const removeRow = (id) => setRows((rs) => rs.length === 1 ? [EMPTY_ROW()] : rs.filter((r) => r.id !== id));
  const clearAll = () => setRows([EMPTY_ROW()]);

  // Poll a single lookup until terminal
  const pollLookup = async (rowId, lookupId) => {
    const started = Date.now();
    while (Date.now() - started < 120000) {
      await new Promise((r) => setTimeout(r, 4000));
      try {
        const { data } = await http.get(`/mobile-lookups/${lookupId}`);
        if (data.state === "settled") {
          updateRow(rowId, { state: "settled", phone: data.phone });
          return true;
        }
        if (data.state === "refunded") {
          updateRow(rowId, { state: "refunded", reason: data.reason });
          return false;
        }
        if (data.state === "error") {
          updateRow(rowId, { state: "error", reason: data.reason });
          return false;
        }
      } catch (_) {}
    }
    updateRow(rowId, { state: "error", reason: "timeout" });
    return false;
  };

  const runOne = async (row) => {
    updateRow(row.id, { state: "pending", phone: null, reason: null });
    try {
      const { data } = await http.post("/mobile-lookups", {
        linkedin_url: row.linkedin_url || null,
        first_name: row.first_name || null,
        last_name: row.last_name || null,
        domain: row.domain || null,
      });
      updateRow(row.id, { lookup_id: data.lookup_id });
      await pollLookup(row.id, data.lookup_id);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Lookup failed";
      updateRow(row.id, { state: "error", reason: msg });
    }
  };

  const runAll = async () => {
    const targets = rows.filter(rowIsValid);
    if (targets.length === 0) {
      toast.error("Add at least one contact to look up.");
      return;
    }
    if (balance < targets.length) {
      toast.error(`Need ${targets.length} credits — you have ${balance}. Buy more from Settings → Find Mobile.`);
      return;
    }
    setRunning(true);
    // Kick all off in parallel — 5 max at a time to stay polite to LeadIQ
    const queue = [...targets];
    const workers = Array.from({ length: Math.min(5, queue.length) }, async () => {
      while (queue.length) {
        const next = queue.shift();
        await runOne(next);
        await loadBalance();
      }
    });
    await Promise.all(workers);
    setRunning(false);
    toast.success("Bulk lookup complete.");
  };

  const onCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) {
      toast.error("CSV must have a header row + at least one data row.");
      return;
    }
    const header = lines[0].toLowerCase().split(",").map((s) => s.trim());
    const idx = {
      first: header.findIndex((h) => /^(first[_\s-]?name|firstname|first)$/.test(h)),
      last: header.findIndex((h) => /^(last[_\s-]?name|lastname|last|surname)$/.test(h)),
      domain: header.findIndex((h) => /^(domain|company[_\s-]?domain|website)$/.test(h)),
      linkedin: header.findIndex((h) => /linkedin/.test(h)),
    };
    const parsed = lines.slice(1).map((line) => {
      const cols = line.split(",").map((s) => s.trim());
      return {
        ...EMPTY_ROW(),
        first_name: idx.first >= 0 ? cols[idx.first] || "" : "",
        last_name: idx.last >= 0 ? cols[idx.last] || "" : "",
        domain: idx.domain >= 0 ? cols[idx.domain] || "" : "",
        linkedin_url: idx.linkedin >= 0 ? cols[idx.linkedin] || "" : "",
      };
    }).filter(rowIsValid);
    if (parsed.length === 0) {
      toast.error("No valid rows found. Need columns: first_name, last_name, domain — OR linkedin_url.");
      return;
    }
    setRows(parsed);
    toast.success(`Loaded ${parsed.length} contact${parsed.length === 1 ? "" : "s"} from CSV.`);
  };

  const copyPhone = async (phone) => {
    try {
      await navigator.clipboard.writeText(phone);
      toast.success("Copied");
    } catch (_) {
      toast.error("Copy failed");
    }
  };

  // Rows that have been processed (settled, refunded, or error)
  const completedRows = rows.filter(
    (r) => r.state === "settled" || r.state === "refunded" || r.state === "error"
  );
  const foundCount = rows.filter((r) => r.state === "settled").length;

  const downloadCsv = () => {
    if (completedRows.length === 0) {
      toast.error("No results to download yet.");
      return;
    }
    const esc = (v) => {
      const s = (v ?? "").toString();
      // RFC 4180: wrap in quotes if it contains ", , or newline; escape " as ""
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ["first_name", "last_name", "domain", "linkedin_url", "mobile", "status", "reason"];
    const statusOf = (r) =>
      r.state === "settled" ? "found" :
      r.state === "refunded" ? "not_found" :
      r.state === "error" ? "error" : "";
    const lines = [
      header.join(","),
      ...completedRows.map((r) =>
        [
          r.first_name,
          r.last_name,
          r.domain,
          r.linkedin_url,
          r.phone || "",
          statusOf(r),
          r.reason || "",
        ].map(esc).join(",")
      ),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
    a.download = `leadiq-mobile-lookups-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${completedRows.length} row${completedRows.length === 1 ? "" : "s"}.`);
  };

  return (
    <main className="max-w-5xl mx-auto px-6 py-10">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase font-mono tracking-widestx text-emerald-700 mb-2">
            Find Mobile · Bulk-friendly
          </div>
          <h1 className="font-display text-3xl font-extrabold text-ink-900 tracking-tight">
            Find verified mobile numbers
          </h1>
          <p className="text-sm text-ink-600 mt-2 max-w-2xl leading-relaxed">
            Look up direct mobile numbers by LinkedIn URL, or by name + company domain. Charged
            only when we find one — refunded on miss. Non-expiring credits.
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase font-mono tracking-widestx text-ink-400 mb-1">
            Balance
          </div>
          <div className="font-mono text-3xl font-extrabold text-ink-900 leading-none">
            {balance}
            <span className="text-sm text-ink-400 font-normal ml-1">credits</span>
          </div>
          <NavLink
            to="/settings/mobile"
            className="text-[11px] font-mono text-emerald-700 hover:underline mt-2 inline-block"
            data-testid="find-mobile-buy-more-link"
          >
            Buy more →
          </NavLink>
        </div>
      </div>

      {/* Card */}
      <div className="bg-white border border-ink-200 rounded-sm p-6">
        {/* Mode tabs */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="inline-flex bg-ink-50 rounded-sm p-1 border border-ink-200">
            <button
              data-testid="mode-manual"
              onClick={() => setMode("manual")}
              className={`px-4 py-1.5 text-[11px] uppercase font-mono tracking-widestx font-semibold rounded-sm transition-colors ${
                mode === "manual" ? "bg-white text-ink-900 shadow-sm" : "text-ink-500 hover:text-ink-700"
              }`}
            >
              Manual
            </button>
            <button
              data-testid="mode-csv"
              onClick={() => setMode("csv")}
              className={`px-4 py-1.5 text-[11px] uppercase font-mono tracking-widestx font-semibold rounded-sm transition-colors flex items-center gap-1.5 ${
                mode === "csv" ? "bg-white text-ink-900 shadow-sm" : "text-ink-500 hover:text-ink-700"
              }`}
            >
              <Upload className="w-3 h-3" /> CSV file
            </button>
          </div>
          <button
            data-testid="find-mobile-clear-all"
            onClick={clearAll}
            disabled={running}
            className="text-[10px] uppercase font-mono tracking-widestx text-ink-500 hover:text-red-600 disabled:opacity-40 flex items-center gap-1.5"
          >
            <Trash2 className="w-3 h-3" /> Clear all
          </button>
        </div>

        {mode === "csv" ? (
          <div className="border-2 border-dashed border-ink-200 rounded-sm p-8 text-center hover:border-emerald-300 transition-colors">
            <Upload className="w-8 h-8 text-ink-300 mx-auto mb-3" />
            <p className="text-sm text-ink-700 mb-1 font-semibold">Upload a CSV of contacts</p>
            <p className="text-xs text-ink-500 mb-4">
              Required columns: <span className="font-mono">first_name</span>, <span className="font-mono">last_name</span>, <span className="font-mono">domain</span> — OR just <span className="font-mono">linkedin_url</span>
            </p>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              onChange={onCsvUpload}
              className="hidden"
              data-testid="find-mobile-csv-input"
            />
            <Button
              onClick={() => fileRef.current?.click()}
              size="sm"
              variant="outline"
              className="rounded-sm text-[11px] uppercase tracking-widestx"
              data-testid="find-mobile-csv-pick"
            >
              <Upload className="w-3 h-3" /> Choose CSV
            </Button>
            {rows.some(rowIsValid) && (
              <p className="text-[11px] font-mono text-emerald-700 mt-4">
                ✓ {validRowCount} row{validRowCount === 1 ? "" : "s"} loaded — scroll down to run.
              </p>
            )}
          </div>
        ) : null}

        {/* Rows */}
        <div className={mode === "csv" ? "mt-6" : ""}>
          {rows.map((r, i) => (
            <div
              key={r.id}
              data-testid={`find-mobile-row-${i}`}
              className={`border rounded-sm p-4 mb-3 transition-colors ${
                r.state === "settled" ? "border-emerald-300 bg-emerald-50/30" :
                r.state === "refunded" || r.state === "error" ? "border-red-200 bg-red-50/30" :
                r.state === "pending" ? "border-blue-300 bg-blue-50/30" :
                "border-ink-200 bg-white"
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-[1fr,auto,1fr,1fr,1fr,auto] gap-3 items-start">
                {/* LinkedIn */}
                <div>
                  <label className="text-[9px] uppercase font-mono tracking-widestx text-ink-400 mb-1 block">LinkedIn URL</label>
                  <div className="relative">
                    <Link2 className="w-3 h-3 text-ink-300 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={r.linkedin_url}
                      onChange={(e) => updateRow(r.id, { linkedin_url: e.target.value })}
                      placeholder="https://linkedin.com/in/jsnow"
                      disabled={running || r.state === "pending"}
                      className="w-full border border-ink-200 rounded-sm h-9 pl-7 pr-2 text-xs font-mono focus:border-emerald-400 focus:outline-none disabled:bg-ink-50"
                    />
                  </div>
                </div>
                <div className="hidden md:flex items-center justify-center pt-6 text-[10px] uppercase font-mono tracking-widestx text-ink-400">or</div>
                <div>
                  <label className="text-[9px] uppercase font-mono tracking-widestx text-ink-400 mb-1 block">First name</label>
                  <div className="relative">
                    <User className="w-3 h-3 text-ink-300 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={r.first_name}
                      onChange={(e) => updateRow(r.id, { first_name: e.target.value })}
                      placeholder="Jon"
                      disabled={running || r.state === "pending"}
                      className="w-full border border-ink-200 rounded-sm h-9 pl-7 pr-2 text-xs focus:border-emerald-400 focus:outline-none disabled:bg-ink-50"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[9px] uppercase font-mono tracking-widestx text-ink-400 mb-1 block">Last name</label>
                  <input
                    type="text"
                    value={r.last_name}
                    onChange={(e) => updateRow(r.id, { last_name: e.target.value })}
                    placeholder="Snow"
                    disabled={running || r.state === "pending"}
                    className="w-full border border-ink-200 rounded-sm h-9 px-2.5 text-xs focus:border-emerald-400 focus:outline-none disabled:bg-ink-50"
                  />
                </div>
                <div>
                  <label className="text-[9px] uppercase font-mono tracking-widestx text-ink-400 mb-1 block">Company domain</label>
                  <div className="relative">
                    <Building2 className="w-3 h-3 text-ink-300 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={r.domain}
                      onChange={(e) => updateRow(r.id, { domain: e.target.value })}
                      placeholder="stark.com"
                      disabled={running || r.state === "pending"}
                      className="w-full border border-ink-200 rounded-sm h-9 pl-7 pr-2 text-xs font-mono focus:border-emerald-400 focus:outline-none disabled:bg-ink-50"
                    />
                  </div>
                </div>
                <div className="flex items-end justify-end pt-5">
                  <button
                    onClick={() => removeRow(r.id)}
                    disabled={running}
                    data-testid={`find-mobile-row-remove-${i}`}
                    className="text-ink-400 hover:text-red-600 disabled:opacity-30 p-1"
                    title="Remove row"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Result strip */}
              {r.state !== "idle" && (
                <div className="mt-3 pt-3 border-t border-ink-100 flex items-center gap-2 text-[11px] font-mono">
                  {r.state === "pending" && (
                    <span className="text-blue-700 flex items-center gap-1.5">
                      <Loader2 className="w-3 h-3 animate-spin" /> Finding mobile… (30–90s)
                    </span>
                  )}
                  {r.state === "settled" && (
                    <>
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      <a href={`tel:${r.phone}`} className="text-emerald-800 font-semibold text-sm">
                        {r.phone}
                      </a>
                      <button
                        onClick={() => copyPhone(r.phone)}
                        className="text-ink-400 hover:text-ink-700"
                        title="Copy"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                      <span className="text-[9px] uppercase tracking-widestx bg-emerald-50 border border-emerald-200 px-1 rounded-sm text-emerald-700 ml-auto">
                        verified · 1 cr
                      </span>
                    </>
                  )}
                  {r.state === "refunded" && (
                    <span className="text-ink-500 flex items-center gap-1.5">
                      <XCircle className="w-3 h-3" /> No mobile found — credit refunded
                    </span>
                  )}
                  {r.state === "error" && (
                    <span className="text-red-600 flex items-center gap-1.5">
                      <XCircle className="w-3 h-3" /> {r.reason || "Error"}
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {mode === "manual" && (
          <button
            onClick={addRow}
            disabled={running}
            data-testid="find-mobile-add-row"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase font-mono tracking-widestx font-semibold text-emerald-700 hover:text-emerald-900 mt-1 disabled:opacity-40"
          >
            <Plus className="w-3.5 h-3.5" /> Add contact
          </button>
        )}

        {/* Run bar */}
        <div className="mt-6 pt-4 border-t border-ink-100 flex items-center justify-between gap-4 flex-wrap">
          <div className="text-[11px] text-ink-500 font-mono">
            {validRowCount} valid contact{validRowCount === 1 ? "" : "s"} · needs {validRowCount} credit{validRowCount === 1 ? "" : "s"}
            {validRowCount > balance && (
              <span className="text-red-600 ml-2">· insufficient balance</span>
            )}
            {completedRows.length > 0 && (
              <span className="text-emerald-700 ml-2">
                · {foundCount}/{completedRows.length} found
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {completedRows.length > 0 && (
              <Button
                onClick={downloadCsv}
                disabled={running}
                variant="outline"
                data-testid="find-mobile-download-csv"
                className="rounded-sm border-emerald-300 text-emerald-800 hover:bg-emerald-50 text-[11px] uppercase tracking-widestx h-10 px-4"
                title="Download results as CSV"
              >
                <Download className="w-3 h-3" /> Download CSV
              </Button>
            )}
            <Button
              onClick={runAll}
              disabled={running || validRowCount === 0 || validRowCount > balance}
              data-testid="find-mobile-run-all"
              className="rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white text-[11px] uppercase tracking-widestx h-10 px-6"
            >
              {running ? (
                <><Loader2 className="w-3 h-3 animate-spin" /> Finding…</>
              ) : (
                <><Phone className="w-3 h-3" /> Find {validRowCount || ""} mobile{validRowCount === 1 ? "" : "s"}</>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Tip banner */}
      <div className="mt-4 bg-emerald-50/50 border border-emerald-200/50 rounded-sm p-4 text-[11px] text-emerald-900 leading-relaxed">
        <strong>Pro tip:</strong> Providing a LinkedIn URL gets <strong>+35% more results</strong> than name + domain alone — LeadIQ uses it to disambiguate contacts across companies.
      </div>
    </main>
  );
}
